import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users2, FolderKanban, UserCog, LogOut, ExternalLink, Menu, X, Newspaper, Mail, Target, Building2, FileText, KeyRound, UserCircle, ClipboardCheck, Briefcase, GraduationCap, Bell, Landmark, BookOpenCheck } from "lucide-react";
import { useAuth } from "@/admin/AuthContext";
import { LOGOS } from "@/data/site";
import ChangePasswordModal from "@/admin/ChangePasswordModal";
import CPMark from "@/components/CPMark";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/leads", label: "Leads", icon: Users2, testid: "nav-leads" },
  { to: "/admin/pipeline", label: "Pipeline", icon: Target, testid: "nav-pipeline" },
  { to: "/admin/companies", label: "Companies", icon: Building2, testid: "nav-companies" },
  { to: "/admin/contracts", label: "Contracts", icon: FileText, testid: "nav-contracts" },
  { to: "/admin/assessment/submissions", label: "Assessments", icon: ClipboardCheck, testid: "nav-assessments" },
  { to: "/admin/careers/applications", label: "Careers", icon: Briefcase, testid: "nav-careers" },
  { to: "/admin/capability-requests", label: "Capability Requests", icon: Landmark, testid: "nav-capability-requests" },
  { to: "/admin/career-services", label: "Career Services", icon: GraduationCap, testid: "nav-career-services" },
  { to: "/admin/programs", label: "Programs & Training", icon: BookOpenCheck, testid: "nav-programs" },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, testid: "nav-blog" },
  { to: "/admin/subscribers", label: "Subscribers", icon: Mail, testid: "nav-subscribers" },
  { to: "/admin/team", label: "Team", icon: UserCog, testid: "nav-team", adminOnly: true },
  { to: "/admin/settings/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications-settings", adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const canChangePassword = user?.auth_provider !== "google";

  const SidebarContent = () => (
    <>
      <div>
        <Link to="/admin" className="flex items-center gap-3 px-6 h-24 border-b border-white/10">
          <CPMark className="w-20 h-20" variant="dark" />
          <span className="font-display font-800 text-white text-base tracking-tightest leading-none">CONSILIUM <span className="text-gold">PRO</span><br /><span className="overline text-[8px] text-white/50">Workspace</span></span>
        </Link>
        <nav className="p-4 space-y-1">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm font-500 transition-colors ${isActive ? "bg-gold text-navy" : "text-white/70 hover:bg-white/5 hover:text-white"}`}
            >
              <n.icon className="w-5 h-5" strokeWidth={1.5} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="p-4 border-t border-white/10">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white transition-colors"><ExternalLink className="w-4 h-4" /> View Website</a>
        <NavLink
          to="/admin/profile"
          data-testid="nav-profile"
          onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}
        >
          <UserCircle className="w-4 h-4" /> Profile & Notifications
        </NavLink>
        {canChangePassword && (
          <button
            data-testid="change-password-btn"
            onClick={() => { setPwOpen(true); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white transition-colors"
          >
            <KeyRound className="w-4 h-4" /> Change Password
          </button>
        )}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 bg-gold flex items-center justify-center shrink-0">
            {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 object-cover" /> : <span className="font-display font-700 text-navy text-sm">{initials}</span>}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-600 truncate">{user?.name}</p>
            <p className="text-white/40 text-xs capitalize">{user?.role}</p>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-lightgray flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-navy flex-col justify-between z-40">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-16 bg-navy flex items-center justify-between px-5 z-40">
        <Link to="/admin" className="flex items-center gap-2"><CPMark className="w-16 h-16" variant="dark" /><span className="font-display font-800 text-white tracking-tightest">CONSILIUM <span className="text-gold">PRO</span></span></Link>
        <button onClick={() => setOpen(!open)} className="text-white p-2" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <aside className="lg:hidden fixed inset-y-0 left-0 w-64 bg-navy flex flex-col justify-between z-50">
          <SidebarContent />
        </aside>
      )}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">
        <Outlet />
      </main>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
